import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { Volume2, VolumeX, Sun, Moon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';

interface SettingsTogglesProps {
  isMuted: boolean;
  onToggleMute: () => void;
}

export const SettingsToggles = ({ isMuted, onToggleMute }: SettingsTogglesProps) => {
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const [mounted, setMounted] = useState(false);

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleToggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    toast({
      title: newTheme === 'dark' ? "Dark mode 🌙" : "Light mode ☀️",
      duration: 1500,
    });
  };

  const handleToggleSound = () => {
    onToggleMute();
    toast({
      title: isMuted ? "Sound enabled 🔊" : "Sound muted 🔇",
      duration: 1500,
    });
  };

  if (!mounted) return null;

  const isDark = theme === 'dark';

  return (
    <TooltipProvider>
      <div className="fixed top-4 right-4 z-50 flex items-center gap-1 p-1 rounded-full bg-background/60 backdrop-blur-md border border-border/50 shadow-lg">
        {/* Theme Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleToggleTheme}
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full hover:bg-primary/20 transition-colors"
            >
              {isDark ? (
                <Sun className="h-4 w-4 text-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-foreground" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{isDark ? 'Light mode' : 'Dark mode'}</p>
          </TooltipContent>
        </Tooltip>

        {/* Sound Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleToggleSound}
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full hover:bg-primary/20 transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4 text-foreground" />
              ) : (
                <Volume2 className="h-4 w-4 text-foreground" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{isMuted ? 'Unmute' : 'Mute'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};
